import moment from "moment"

import { CAMEL_CASE_REGEX, ONLY_ALPHA, ONLY_DIGITS } from "../constants/regexRepo"
import { AcceptedFileOptions, API_DATE_FORMAT, VALID_DATE_FORMATS } from "../constants/common"
import { EnumItem } from "../types/common"

export const debounce = (func: Function, timeout = 400) => {
  let timer: any
  return (...args: any[]) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, timeout)
  }
}

export const lowerCaseFirstLetter = (name: string) => {
  return name.charAt(0).toLowerCase() + name.slice(1)
}

export const filterDeletedRecords = (array: any[]) => {
  return array?.filter((item: any) => {
    return item.delete === false
  })
}

export const getUUID = () => {
  const uint32 = window.crypto.getRandomValues(new Uint32Array(1))[0]
  return uint32.toString(16)
}

export const getRandomNumber = () => {
  return -Math.floor(Math.random() * 1000000000)
}

export const titleFromKey = (key: string) => {
  const str = key.replace(CAMEL_CASE_REGEX, "$1 $2")
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const downloadFileFromBlob = (blob: any, fileName: string) => {
  // generate file
  const a = document.createElement("a")
  const url = window.URL.createObjectURL(new Blob([blob as BlobPart], { type: "application/pdf" }))
  // download file
  a.href = url
  a.download = fileName
  a.click()
  // cleanup
  window.URL.revokeObjectURL(url)
  a.remove()
}

export const getPayloadDateFormat = (_: any) => {
  if (!_) return undefined
  return moment(_).format(API_DATE_FORMAT)
}

export const getEventCodeColor = (code: string) => {
  switch (code?.toLowerCase()) {
    case "addition":
      return "green"
    case "attributechange":
      return "#2cb0b5"
    case "deletion":
      return "red"
    case "restore":
      return "blue"
    default:
      return "black"
  }
}

export const sortColumn = (rowA: any, rowB: any, field: any) => {
  if (rowA.original[field] > rowB.original[field]) return -1
  if (rowB.original[field] > rowA.original[field]) return 1
  return null
}

export const getAcceptedMimeTypes = (types?: string[]) => {
  if (!types?.length) return AcceptedFileOptions
  return Object.keys(AcceptedFileOptions).reduce((prev: any, curr: string) => {
    const mimeTypes = { ...prev }
    if (types.includes(curr)) {
      mimeTypes[curr] = AcceptedFileOptions[curr as keyof typeof AcceptedFileOptions]
    }
    return mimeTypes
  }, {})
}

export function camelToKebab(str: string) {
  return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase()
}

export const processDateFormat = (date: string) => {
  if (!date) return null
  return moment(date, VALID_DATE_FORMATS).isValid() ? moment(date, VALID_DATE_FORMATS).toDate() : null
}

export const isDateValid = (date: string | undefined) => {
  if (!date) return false
  return moment(date, VALID_DATE_FORMATS).isValid()
}

export const isCUSIP9FormatValid = (CUSIP: string): boolean => {
  if (!CUSIP || CUSIP.length !== 9) return false
  let sum = 0
  for (let i = 0; i < 9; i++) {
    let value
    const c = CUSIP[i]

    if (ONLY_DIGITS.test(c)) {
      value = c.charCodeAt(0) - "0".charCodeAt(0)
    } else if (ONLY_ALPHA.test(c)) {
      value = c.toUpperCase().charCodeAt(0) - "A".charCodeAt(0) + 10
    } else if (c === "*") {
      value = 36
    } else if (c === "@") {
      value = 37
    } else if (c === "#") {
      value = 38
    } else {
      return false
    }

    if (i % 2 === 1) value *= 2

    sum += Math.floor(value / 10) + (value % 10)
  }

  return sum % 10 === 0
}

// Remove NA (Not Assigned) and O (Other) values from the pricing frequncy 
export const updateFrequencyEnum = (data: EnumItem[]): EnumItem[] => {
  let frequencyEnumArray = [...data]
  const updatedFrequencyEnum : EnumItem | undefined = frequencyEnumArray.find((item: any) => item.key === "FrequencyEnum")
  if(updatedFrequencyEnum){
    updatedFrequencyEnum.value = updatedFrequencyEnum?.value?.filter((item: any) => item.value !== 'NA' && item.value !== 'O') 
    frequencyEnumArray = [...frequencyEnumArray.filter((item: any) => item.key !== 'FrequencyEnum' ), updatedFrequencyEnum]
  }
  return frequencyEnumArray
}

export const findEnumKeyByValue = <T extends object>(enumObj: T, value: string): string | undefined => {
  // require pipe delimited values due to UITREP and REPLCE
  return Object.keys(enumObj).filter((key) => enumObj[key as keyof T] === value).join('|');
};